import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  useTheme,
  useMediaQuery,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
  Dialog,
  DialogContent
} from '@mui/material';
import { Menu as MenuIcon, Settings, Logout, ContactSupportOutlined, Close } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const Navbar = ({ onMenuClick }) => {
  const { user, selectedOrganisation, logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = () => {
    handleMenuClose();
    logout();
  };

  const handleSettings = () => {
    handleMenuClose();
    navigate('/administrators');
  };

  return (
    <AppBar 
      position="fixed" 
      sx={{ 
        zIndex: theme.zIndex.drawer + 1,
        background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 50%, #0d47a1 100%)',
        boxShadow: '0 4px 20px rgba(25, 118, 210, 0.3)',
        height: '64px'
      }}
    >
      <Toolbar sx={{ minHeight: '64px !important', height: '64px' }}>
        {isMobile && (
          <IconButton
            color="inherit"
            edge="start"
            onClick={onMenuClick}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
        )}

        <Typography 
          variant="h6" 
          noWrap 
          sx={{ 
            flexGrow: 1, 
            fontWeight: 600,
            fontSize: { xs: '0.9rem', sm: '1.1rem' }
          }}
        >
          {selectedOrganisation?.name || 'UNFTP Organisation Interface'}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Help">
            <IconButton color="inherit" onClick={() => setHelpOpen(true)} size="small">
              <ContactSupportOutlined />
            </IconButton>
          </Tooltip>
          <Tooltip title="Account">
            <span>
              <IconButton 
                color="inherit" 
                onClick={handleMenuOpen}
                size="small"
                sx={{ ml: 1 }}
              >
                <Avatar 
                  sx={{ 
                    width: 32, 
                    height: 32, 
                    bgcolor: 'rgba(255,255,255,0.2)',
                    fontSize: '0.9rem'
                  }}
                >
                  {user?.email?.charAt(0).toUpperCase() || 'U'}
                </Avatar>
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            sx: { 
              mt: 1.5,
              minWidth: 220,
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              border: '1px solid #e2e8f0'
            }
          }}
        >
          <MenuItem disabled sx={{ opacity: 1, cursor: 'default' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="body2" fontWeight={600}>
                {user?.email}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedOrganisation?.name || 'No organisation'}
              </Typography>
            </Box>
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={handleSettings}
            sx={{
              borderRadius: 1,
              mx: 1,
              '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' }
            }}
          >
            <Settings fontSize="small" sx={{ mr: 2 }} />
            Settings
          </MenuItem>
          <MenuItem 
            onClick={handleLogout} 
            sx={{ 
              borderRadius: 1, 
              mx: 1,
              '&:hover': {
                backgroundColor: 'rgba(211, 47, 47, 0.08)',
              }
            }}
          >
            <Logout fontSize="small" sx={{ mr: 2, color: 'error.main' }} />
            <Typography color="error">Logout</Typography>
          </MenuItem>
        </Menu>
      </Toolbar>

      <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} maxWidth="xs" fullWidth>
        <IconButton
          onClick={() => setHelpOpen(false)}
          size="small"
          sx={{ position: 'absolute', top: 8, right: 8, color: 'text.secondary' }}
        >
          <Close fontSize="small" />
        </IconButton>
        <DialogContent sx={{ textAlign: 'center', py: 5, px: 4 }}>
          <Box component="img" src="/logo.png" alt="UNF*CKING the Planet" sx={{ width: 220, maxWidth: '100%', mb: 4 }} />

          <Typography variant="body1" fontWeight={600}>
            UNFTP email :&nbsp;
            <Box component="a" href="mailto:support@unftp.ngo" sx={{ color: 'inherit', textDecoration: 'none' }}>
              support@unftp.ngo
            </Box>
          </Typography>
          <Divider sx={{ width: 120, mx: 'auto', borderBottomWidth: 2, my: 3 }} />

          <Typography variant="body1" fontWeight={600}>
            Group admin login :&nbsp;
            <Box component="a" href="https://8664.wiki" target="_blank" rel="noopener noreferrer" sx={{ color: 'inherit', textDecoration: 'none' }}>
              8664.wiki
            </Box>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Make a link on your social media or website, and get the admin to
            favourite it on their phone.
          </Typography>
          <Divider sx={{ width: 120, mx: 'auto', borderBottomWidth: 2, mt: 3 }} />
        </DialogContent>
      </Dialog>
    </AppBar>
  );
};

export default Navbar;